/**
 * The client's comments on a card, read live from the card by
 * `GET /api/editor/work/:id/client-feedback`. Its own fetch, so a slow or broken
 * Trello read only ever touches this section; the page around it has already
 * loaded. Shown on the card details page and under the read-only tile of a card
 * handed in or further along.
 *
 * @module web/screens/ClientFeedback
 */
import { useEffect, useState } from "react";
import { get } from "../api/client.ts";
import type { CardClientFeedback } from "../api/types.ts";
import { useI18n } from "../i18n/index.tsx";
import { LinkifiedText } from "./safeMarkdown.tsx";

type State = { kind: "loading" } | { kind: "unavailable" } | { kind: "ready"; comments: CardClientFeedback["comments"] };

export function ClientFeedback({ workItemId, alongsideFreeframe = false }: { workItemId: string; alongsideFreeframe?: boolean }) {
  const i18n = useI18n();
  const { t } = i18n;
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    let live = true;
    setState({ kind: "loading" });
    get<CardClientFeedback>(`/api/editor/work/${encodeURIComponent(workItemId)}/client-feedback`)
      .then((r) => { if (live) setState(r.available ? { kind: "ready", comments: r.comments } : { kind: "unavailable" }); })
      .catch(() => { if (live) setState({ kind: "unavailable" }); });
    return () => { live = false; };
  }, [workItemId]);

  return (
    <section className="cd-panel cd-feedback" data-section="client-feedback" data-state={state.kind}>
      <div className="cd-tag">
        <span>{t("cardDetails.feedbackTitle")}</span>
        {state.kind === "ready" && state.comments.length > 0 && <span>{state.comments.length}</span>}
      </div>
      {state.kind === "loading" && <p className="ed-muted">{t("cardDetails.feedbackLoading")}</p>}
      {state.kind === "unavailable" && <p className="ed-muted">{t("cardDetails.feedbackUnavailable")}</p>}
      {state.kind === "ready" && state.comments.length === 0 && <p className="ed-muted">{t("cardDetails.feedbackEmpty")}</p>}
      {state.kind === "ready" && state.comments.map((c) => (
        <article className="cd-comment" key={c.id} data-comment={c.id}>
          <div className="cd-comment-head">
            <b>{c.author}</b>
            <time dateTime={c.at}>{i18n.dateTime(c.at, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</time>
          </div>
          <p><LinkifiedText text={c.text} /></p>
        </article>
      ))}
      {alongsideFreeframe && <p className="cd-hint">{t("cardDetails.feedbackFreeframeHint")}</p>}
    </section>
  );
}
